/**
 * FactionSystem — Manages party reputation with each faction.
 * Reputation ranges from -100 (Hostile) to +100 (Honored).
 * All faction definitions are loaded from src/data/factions.json.
 *
 * @module FactionSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import FACTIONS_DATA from '../data/factions.json';

// ─── Standing thresholds ──────────────────────────────────────────────────────

const STANDINGS = [
  { label: 'Hostile',    color: '#e53935', min: -100, max: -51  },
  { label: 'Unfriendly', color: '#fb8c00', min: -50,  max: -11  },
  { label: 'Neutral',    color: '#9e9e9e', min: -10,  max: 10   },
  { label: 'Friendly',   color: '#43a047', min: 11,   max: 50   },
  { label: 'Honored',    color: '#fdd835', min: 51,   max: 100  },
];

// ─── FactionSystem ────────────────────────────────────────────────────────────

const FactionSystem = {
  /**
   * Faction definitions keyed by id.
   * @type {Map<string, object>}
   */
  _factions: new Map(),

  /**
   * Current reputation with each faction. Maps factionId → number (-100..100).
   * @type {Map<string, number>}
   */
  _reputations: new Map(),

  /**
   * Whether the system has been initialized.
   * @type {boolean}
   */
  _initialized: false,

  // ─── Core API ──────────────────────────────────────────────────────────────

  /**
   * Initialize FactionSystem. Loads faction definitions and sets initial
   * reputations from each faction's defaultReputation value.
   * Safe to call multiple times.
   */
  init() {
    if (this._initialized) return;

    for (const faction of FACTIONS_DATA) {
      this._factions.set(faction.id, Object.freeze({ ...faction }));
      this._reputations.set(faction.id, faction.defaultReputation ?? 0);
    }

    this._initialized = true;
    DebugLog.info(`[faction] FactionSystem initialized; factions=${this._factions.size}`);

    // Listen for combat victories — killing enemies with a faction affiliation
    // reduces rep with that faction and increases rep with allied factions.
    EventBus.on('combat:victory', (data) => {
      this._onCombatVictory(data);
    });

    // Register debug helpers
    this._registerDebugCommands();
  },

  /**
   * Get current reputation with a faction.
   * @param {string} factionId
   * @returns {number} Reputation value (-100 to 100), or 0 if unknown faction
   */
  getReputation(factionId) {
    if (!this._reputations.has(factionId)) {
      DebugLog.warn(`[faction] getReputation: unknown factionId="${factionId}"`);
      return 0;
    }
    return this._reputations.get(factionId);
  },

  /**
   * Modify reputation with a faction by delta. Clamps result to -100..100.
   * Emits 'faction:repChanged'.
   *
   * @param {string} factionId
   * @param {number} delta — Positive or negative integer
   * @param {string} [reason] — Human-readable reason for logging
   */
  modifyReputation(factionId, delta, reason = '') {
    if (!this._factions.has(factionId)) {
      DebugLog.warn(`[faction] modifyReputation: unknown factionId="${factionId}"`);
      return;
    }

    const prev = this._reputations.get(factionId) ?? 0;
    const next = Math.max(-100, Math.min(100, prev + delta));
    this._reputations.set(factionId, next);

    const standing = this.getStanding(factionId);

    DebugLog.event(
      `[faction] Rep changed; faction=${factionId} prev=${prev} delta=${delta} ` +
      `next=${next} standing=${standing.label}${reason ? ` reason="${reason}"` : ''}`
    );

    EventBus.emit('faction:repChanged', {
      factionId,
      prev,
      next,
      delta,
      standing,
      reason,
    });
  },

  /**
   * Get the standing label and color for a faction based on current reputation.
   * @param {string} factionId
   * @returns {{ label: string, color: string }}
   */
  getStanding(factionId) {
    const rep = this.getReputation(factionId);

    for (const standing of STANDINGS) {
      if (rep >= standing.min && rep <= standing.max) {
        return { label: standing.label, color: standing.color };
      }
    }

    // Fallback (should never happen given full -100..100 coverage)
    return { label: 'Neutral', color: '#9e9e9e' };
  },

  /**
   * Get all faction IDs where current reputation is below -30.
   * @returns {string[]}
   */
  getHostileFactions() {
    const hostile = [];
    for (const [id, rep] of this._reputations) {
      if (rep < -30) hostile.push(id);
    }
    return hostile;
  },

  /**
   * Get the relationship between two factions based on their faction definitions.
   * @param {string} factionId1
   * @param {string} factionId2
   * @returns {'ally'|'enemy'|'neutral'}
   */
  getRelationship(factionId1, factionId2) {
    const f1 = this._factions.get(factionId1);
    if (!f1) return 'neutral';

    if (f1.allyFactions?.includes(factionId2))  return 'ally';
    if (f1.enemyFactions?.includes(factionId2)) return 'enemy';

    return 'neutral';
  },

  /**
   * Get all faction definitions with current reputation and standing attached.
   * @returns {Array<object>}
   */
  getAllFactions() {
    return [...this._factions.values()].map(faction => ({
      ...faction,
      reputation: this.getReputation(faction.id),
      standing:   this.getStanding(faction.id),
    }));
  },

  // ─── Serialization ─────────────────────────────────────────────────────────

  /**
   * Serialize reputation state for the save system.
   * @returns {{ reputations: Record<string, number> }}
   */
  serialize() {
    return {
      reputations: Object.fromEntries(this._reputations),
    };
  },

  /**
   * Restore reputation state from save data.
   * @param {{ reputations: Record<string, number> }} data
   */
  deserialize(data) {
    if (!data?.reputations) return;

    for (const [id, rep] of Object.entries(data.reputations)) {
      if (this._factions.has(id)) {
        this._reputations.set(id, Math.max(-100, Math.min(100, rep)));
      }
    }

    DebugLog.info(
      `[faction] FactionSystem deserialized; ` +
      `factions=${Object.keys(data.reputations).length}`
    );
  },

  /**
   * Reset all reputations to their faction defaults (used on new game).
   */
  reset() {
    for (const [id, faction] of this._factions) {
      this._reputations.set(id, faction.defaultReputation ?? 0);
    }
    DebugLog.info('[faction] FactionSystem reset to defaults');
  },

  // ─── Internal helpers ──────────────────────────────────────────────────────

  /**
   * Handle a combat:victory event. If the defeated enemies have a faction field,
   * reduce reputation with that faction by -5 and increase reputation with
   * all factions that consider the defeated faction an enemy by +2.
   * @param {object} data — Combat victory event data
   */
  _onCombatVictory(data) {
    const enemies = data?.enemies ?? data?.defeatedEnemies ?? [];
    if (!Array.isArray(enemies)) return;

    // Collect distinct factions from the defeated enemies
    const defeatedFactions = new Set();
    for (const enemy of enemies) {
      if (enemy?.faction && this._factions.has(enemy.faction)) {
        defeatedFactions.add(enemy.faction);
      }
    }

    for (const defeatedFactionId of defeatedFactions) {
      // Reduce rep with the faction whose members were defeated
      this.modifyReputation(defeatedFactionId, -5, 'combat:victory');

      // Increase rep with factions that are enemies of the defeated faction
      for (const [id, faction] of this._factions) {
        if (id === defeatedFactionId) continue;
        if (faction.enemyFactions?.includes(defeatedFactionId)) {
          this.modifyReputation(id, +2, `allied victory over ${defeatedFactionId}`);
        }
      }
    }
  },

  /**
   * Register debug commands on window.__debug.
   */
  _registerDebugCommands() {
    if (typeof window === 'undefined') return;

    window.__debug = window.__debug ?? {};

    /**
     * Set reputation with a faction directly (debug).
     * @param {string} factionId
     * @param {number} value — -100 to 100
     */
    window.__debug.setRep = (factionId, value) => {
      if (!this._factions.has(factionId)) {
        const msg = `setRep: unknown factionId="${factionId}"`;
        DebugLog.warn(`[faction] ${msg}`);
        return msg;
      }

      const clamped = Math.max(-100, Math.min(100, Number(value)));
      this._reputations.set(factionId, clamped);
      const standing = this.getStanding(factionId);

      const msg = `[faction] setRep: ${factionId}=${clamped} (${standing.label})`;
      DebugLog.info(msg);

      EventBus.emit('faction:repChanged', {
        factionId,
        prev: clamped,
        next: clamped,
        delta: 0,
        standing,
        reason: 'debug:setRep',
      });

      return msg;
    };

    /**
     * List all factions with their current reputation and standing (debug).
     * @returns {Array<object>}
     */
    window.__debug.listFactions = () => {
      return this.getAllFactions().map(f => ({
        id:         f.id,
        name:       f.name,
        icon:       f.icon,
        reputation: f.reputation,
        standing:   f.standing.label,
        color:      f.standing.color,
      }));
    };

    /**
     * Open the Faction panel (debug).
     */
    window.__debug.openFactions = () => {
      if (window.__factionPanel) {
        window.__factionPanel.show();
      } else {
        DebugLog.warn('[faction] openFactions: FactionPanel not initialized');
      }
    };

    DebugLog.info('[faction] Debug commands registered: setRep, listFactions, openFactions');
  },
};

export default FactionSystem;
